import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import { toast } from "sonner";
import { VocFeatures } from "../components/VocFeatures";
import * as Icons from "lucide-react";
import { EmailTemplates } from "./EmailTemplates";
import { INTERESTED_SOLUTIONS, ROLE_LABELS } from "../lib/constants";
import { AttendanceSettings } from "../components/AttendanceSettings";
import { useAuth } from "../context/AuthContext";

const FIELDS = [
  ["solar_zone_rating", "Solar Zone Rating", "VIC = 1.185"],
  ["solar_deeming_years", "Deeming Years", "2026 = 5"],
  ["stc_price", "STC Price ($)", "Value per STC — used for quotes AND tax invoices (recalculates everywhere)"],
  ["battery_stc_per_kwh", "Battery STC per kWh", "e.g. 7"],
  ["vic_rebate", "VIC Solar Rebate ($)", "1400"],
  ["vic_loan", "VIC Interest-Free Loan ($)", "1400"],
  ["feed_in_tariff", "Feed-in Tariff ($/kWh)", "e.g. 0.033"],
  ["usage_rate", "Usage Rate ($/kWh)", "e.g. 0.30"],
  ["daily_sun_factor", "Daily Sun Factor (kWh/kW)", "e.g. 3.8"],
];

function Collapse({ title, icon, defaultOpen = false, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = Icons[icon] || Icons.Settings2;
  return (
    <div data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testId}-toggle`}
        className="w-full flex items-center gap-3 bg-card border border-border rounded-md px-5 py-3.5 shadow-sm text-left hover:bg-muted/40 transition-colors">
        <Icon size={17} className="text-secondary shrink-0" />
        <span className="font-head font-semibold text-sm flex-1">{title}</span>
        <Icons.ChevronDown size={17} className={`text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-2 space-y-2">{children}</div>}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [mbAvailable, setMbAvailable] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
    api.get("/mailboxes").then((r) => { setMbAvailable(!!r.data.available); setMailboxes(r.data.mailboxes || []); }).catch(() => {});
  }, []);
  if (!s) return <div className="text-muted-foreground">Loading…</div>;

  const save = async () => {
    try { const { auto_assign, ...rest } = s; await api.put("/settings", rest); toast.success("Settings saved"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-3 max-w-3xl">
      <div className="pb-2"><h1 className="text-3xl font-head font-extrabold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Click a section to expand it</p></div>

      <Collapse title="Rebates & Calculation Values" icon="Calculator" defaultOpen testId="collapse-calculations">
      <div className="bg-card border border-border rounded-md p-6 shadow-sm grid sm:grid-cols-2 gap-4" data-testid="settings-form">
        {FIELDS.map(([k, l, hint]) => (
          <div key={k}><label className="label-caps">{l}</label>
            <input type="number" step="0.001" value={s[k]} onChange={(e) => setS({ ...s, [k]: parseFloat(e.target.value) })}
              data-testid={`setting-${k}`} className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
            <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div></div>
        ))}
      </div>
      </Collapse>

      <Collapse title="Database & Storage Usage" icon="Database" defaultOpen testId="collapse-storage"><StorageUsage limit={s.storage_limit_mb} onLimit={(v) => setS({ ...s, storage_limit_mb: v })} onSave={save} /></Collapse>

      <Collapse title="Lost Reason Categories" icon="XCircle" testId="collapse-lost-reasons">
        <div className="bg-card border border-border rounded-md p-6 shadow-sm space-y-3">
          <p className="text-sm text-muted-foreground">Consultants must pick one of these when a lead is moved to <b>Lost</b>. One reason per line.</p>
          <textarea rows={8} value={(s.lost_reasons || []).join("\n")} data-testid="setting-lost-reasons"
            onChange={(e) => setS({ ...s, lost_reasons: e.target.value.split("\n") })}
            onBlur={() => setS({ ...s, lost_reasons: (s.lost_reasons || []).map((x) => x.trim()).filter(Boolean) })}
            className="w-full px-3 py-2 rounded-md border border-input bg-background font-mono text-sm" />
          <button onClick={save} data-testid="save-lost-reasons" className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-sm">Save</button>
        </div>
      </Collapse>

      <Collapse title="Company Details (shown on quotes)" icon="Building2" testId="collapse-company">
      <div className="bg-card border border-border rounded-md p-6 shadow-sm">
        <div className="grid sm:grid-cols-2 gap-4">
          {["name", "abn", "phone", "email", "website", "address"].map((k) => (
            <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
              <label className="label-caps">{k}</label>
              <input value={s.company?.[k] || ""} onChange={(e) => setS({ ...s, company: { ...s.company, [k]: e.target.value } })}
                data-testid={`company-${k}`} className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
          ))}
        </div>
        <h3 className="font-semibold text-sm mt-6 mb-3">Tax invoice bank details</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {[["bank_account_name", "Account name"], ["bank_bsb", "BSB"], ["bank_account_number", "Account number"], ["bank_reference", "Payment reference ({{invoice_no}})"]].map(([key, label]) => (
            <label key={key} className="text-sm">{label}<input data-testid={`company-${key.replaceAll("_", "-")}`} value={s.company?.[key] || ""}
              onChange={(e) => setS({ ...s, company: { ...s.company, [key]: e.target.value } })}
              className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></label>
          ))}
        </div>
        <button onClick={save} data-testid="save-company" className="mt-4 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold">Save company details</button>
      </div>
      </Collapse>

      <Collapse title="Quote expiry" icon="CalendarClock" testId="collapse-quote-expiry">
        <div className="p-6 space-y-4">
          <label className="block text-sm">Default validity (days from preparation)
            <input type="number" min="1" max="365" value={s.quote_expiry?.default_days ?? 15} data-testid="expiry-default-days"
              onChange={(e) => setS({ ...s, quote_expiry: { ...s.quote_expiry, default_days: Number(e.target.value) } })}
              className="block w-32 mt-1 px-3 py-2 rounded-md border border-input bg-background" /></label>
          {[["email_customer", "Email customer when an option expires"], ["email_rep", "Email sales representative when an option expires"]].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid={`expiry-${key.replaceAll("_", "-")}`} checked={s.quote_expiry?.[key] !== false}
              onChange={(e) => setS({ ...s, quote_expiry: { ...s.quote_expiry, [key]: e.target.checked } })} />{label}</label>
          ))}
          <p className="text-xs text-muted-foreground" data-testid="expiry-settings-note">New options use this default. Existing dates stay unchanged. Expiry is at the end of the selected day in Melbourne; signing remains available. Templates are in Email Templates below.</p>
          <button onClick={save} data-testid="save-expiry-settings" className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold">Save expiry settings</button>
        </div>
      </Collapse>

      {["super_admin", "admin", "admin_manager"].includes(user?.role) && <Collapse title="Attendance & static office IPs" icon="Clock3" testId="collapse-attendance"><div className="p-5"><AttendanceSettings /></div></Collapse>}

      <Collapse title="Job Pack Contacts" icon="ClipboardList" testId="collapse-job-pack-contacts">
      <div className="bg-card border border-border rounded-md p-6 shadow-sm space-y-3" data-testid="job-pack-contacts-form">
        <p className="text-sm text-muted-foreground">Saved installer / warehouse contacts. They appear as quick-pick options in <b>Prepost → Send Job Pack</b>. The recipient always defaults to <b>info@griansolarpower.com.au</b> and can still be changed per send.</p>
        {(s.job_pack_contacts || []).map((c, i) => (
          <div key={i} className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-2 items-center">
            <input value={c.name || ""} placeholder="Name" data-testid={`job-pack-contact-name-${i}`}
              onChange={(e) => { const arr = [...(s.job_pack_contacts || [])]; arr[i] = { ...arr[i], name: e.target.value }; setS({ ...s, job_pack_contacts: arr }); }}
              className="px-3 py-2 rounded-md border border-input bg-background text-sm" />
            <input value={c.email || ""} placeholder="email@example.com" data-testid={`job-pack-contact-email-${i}`}
              onChange={(e) => { const arr = [...(s.job_pack_contacts || [])]; arr[i] = { ...arr[i], email: e.target.value }; setS({ ...s, job_pack_contacts: arr }); }}
              className="px-3 py-2 rounded-md border border-input bg-background text-sm" />
            <button type="button" data-testid={`job-pack-contact-remove-${i}`} title="Remove contact"
              onClick={() => setS({ ...s, job_pack_contacts: (s.job_pack_contacts || []).filter((_, j) => j !== i) })}
              className="px-3 py-2 rounded-md border border-border text-sm text-destructive hover:bg-destructive/10"><Icons.Trash2 size={14} /></button>
          </div>
        ))}
        <div className="flex gap-2 pt-1">
          <button type="button" data-testid="job-pack-contact-add"
            onClick={() => setS({ ...s, job_pack_contacts: [...(s.job_pack_contacts || []), { name: "", email: "" }] })}
            className="px-4 py-2 rounded-md border border-border font-semibold text-sm flex items-center gap-1.5"><Icons.Plus size={14} /> Add contact</button>
          <button onClick={save} data-testid="save-job-pack-contacts" className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-sm">Save</button>
        </div>
      </div>
      </Collapse>

      <Collapse title="Site Photo Checklist" icon="Camera" testId="collapse-site-photos">
      <div className="bg-card border border-border rounded-md p-6 shadow-sm space-y-3" data-testid="site-photos-form">
        <p className="text-sm text-muted-foreground">The required photos checked on the <b>Pre/Post queue</b> and each project's <b>Prepost</b> tab. Matching is automatic — a document whose name or label contains the keyword (e.g. a file named "Meter photo…") ticks the "Meter photo" item.</p>
        {(s.required_site_photos || []).map((p, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto] gap-2 items-center">
            <input value={p} data-testid={`site-photo-${i}`} placeholder="e.g. Meter photo"
              onChange={(e) => { const arr = [...(s.required_site_photos || [])]; arr[i] = e.target.value; setS({ ...s, required_site_photos: arr }); }}
              className="px-3 py-2 rounded-md border border-input bg-background text-sm" />
            <button type="button" data-testid={`site-photo-remove-${i}`} title="Remove item"
              onClick={() => setS({ ...s, required_site_photos: (s.required_site_photos || []).filter((_, j) => j !== i) })}
              className="px-3 py-2 rounded-md border border-border text-sm text-destructive hover:bg-destructive/10"><Icons.Trash2 size={14} /></button>
          </div>
        ))}
        <div className="flex flex-wrap gap-1.5">
          {["Power bill", "Switchboard photo", "Meter photo", "Battery location 1", "Battery location 2", "Roof layout", "Property front"]
            .filter((x) => !(s.required_site_photos || []).includes(x)).map((x) => (
            <button type="button" key={x} data-testid={`site-photo-suggest-${x.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setS({ ...s, required_site_photos: [...(s.required_site_photos || []), x] })}
              className="px-2.5 py-1 rounded-full border border-dashed border-border text-xs hover:bg-muted">+ {x}</button>
          ))}
        </div>
        <div className="flex gap-2 pt-1">
          <button type="button" data-testid="site-photo-add"
            onClick={() => setS({ ...s, required_site_photos: [...(s.required_site_photos || []), ""] })}
            className="px-4 py-2 rounded-md border border-border font-semibold text-sm flex items-center gap-1.5"><Icons.Plus size={14} /> Add item</button>
          <button onClick={save} data-testid="save-site-photos" className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-sm">Save</button>
        </div>
      </div>
      </Collapse>

      <Collapse title="Email Notifications (Outlook 365)" icon="Mail" testId="collapse-email">
      <div className="bg-card border border-border rounded-md p-6 shadow-sm" data-testid="email-settings">
        <p className="text-xs text-muted-foreground mb-4">Automated emails sent via Microsoft Graph from your business mailbox. Credentials are configured securely on the server.</p>
        {(() => {
          const ec = s.email_config || {};
          const setEc = (patch) => setS({ ...s, email_config: { ...ec, ...patch } });
          const toggles = [
            ["notify_customer_on_create", "Email customer on new enquiry (thank-you)"],
            ["notify_admin_on_create", "Email admin/office when a new lead arrives"],
            ["notify_customer_on_assign", "Email customer when their lead is assigned to a consultant"],
            ["notify_rep_on_assign", "Email the sales rep when a lead is assigned to them"],
          ];
          return (
            <div className="space-y-4">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input type="checkbox" checked={!!ec.enabled} onChange={(e) => setEc({ enabled: e.target.checked })} data-testid="email-enabled" />
                Enable automated emails
              </label>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="label-caps">Admin / office recipients (comma separated)</label>
                  <input value={(ec.admin_recipients || []).join(", ")}
                    onChange={(e) => setEc({ admin_recipients: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                    data-testid="email-admin-recipients" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
                <div><label className="label-caps">Company phone (shown to customer)</label>
                  <input value={ec.company_phone || ""} onChange={(e) => setEc({ company_phone: e.target.value })}
                    data-testid="email-company-phone" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
                <div><label className="label-caps">Business &amp; marketing enquiries phone</label>
                  <input value={ec.business_phone || ""} onChange={(e) => setEc({ business_phone: e.target.value })}
                    data-testid="email-business-phone" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" placeholder="0485 976 518" />
                  <div className="text-[11px] text-muted-foreground mt-0.5">Shown in the customer thank-you email &amp; booking page.</div></div>
                <div><label className="label-caps">After-sales support &amp; complaints phone</label>
                  <input value={ec.support_phone || ""} onChange={(e) => setEc({ support_phone: e.target.value })}
                    data-testid="email-support-phone" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" placeholder="03 7047 6525" />
                  <div className="text-[11px] text-muted-foreground mt-0.5">Shown in the customer thank-you email &amp; booking page.</div></div>
              </div>
              <div><label className="label-caps">System mailbox for automated emails</label>
                {mbAvailable && mailboxes.length ? (
                  <select value={ec.system_sender || ""} onChange={(e) => setEc({ system_sender: e.target.value })}
                    data-testid="email-system-sender" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                    <option value="">Server default</option>
                    {mailboxes.map((m) => <option key={m.email} value={m.email}>{m.name} ({m.email})</option>)}
                  </select>
                ) : (
                  <input type="email" value={ec.system_sender || ""} onChange={(e) => setEc({ system_sender: e.target.value })}
                    data-testid="email-system-sender" placeholder="e.g. info@griansolarpower.au (blank = server default)"
                    className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
                )}
                <div className="text-[11px] text-muted-foreground mt-0.5">Automated emails (new-enquiry thank-you, office alerts) are sent from this mailbox. Quote &amp; assignment emails always use the assigned consultant's own mailbox.</div></div>
              <div><label className="label-caps">Send "test" emails to</label>
                {mbAvailable && mailboxes.length ? (
                  <select value={ec.test_recipient || ""} onChange={(e) => setEc({ test_recipient: e.target.value })}
                    data-testid="email-test-recipient" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                    <option value="">Default (admin@griansolarpower.au)</option>
                    {mailboxes.map((m) => <option key={m.email} value={m.email}>{m.name} ({m.email})</option>)}
                  </select>
                ) : (
                  <input type="email" value={ec.test_recipient || ""} onChange={(e) => setEc({ test_recipient: e.target.value })}
                    data-testid="email-test-recipient" placeholder="admin@griansolarpower.au"
                    className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
                )}
                <div className="text-[11px] text-muted-foreground mt-0.5">When you click "Send test" on a user's mailbox, the test lands here (a <b>.au</b> CRM inbox). Blank = admin@griansolarpower.au. This is separate from your GSuite <b>.com.au</b> office address.</div></div>
              <div className="flex items-start gap-2.5 bg-primary/5 border border-primary/15 rounded-md p-3 text-xs text-muted-foreground" data-testid="booking-info">
                <Icons.CalendarClock size={16} className="text-primary mt-0.5 shrink-0" />
                <span>
                  <b className="text-foreground">Callback booking:</b> when the "thank-you" email is on, every customer automatically receives a personalised
                  "Schedule my free callback" button. You can also copy each lead's unique booking link from the <b>lead page → Overview</b> to share by SMS or during a call.
                  Bookings appear on the lead (Overview banner), the calendar, and follow the lead when it's reassigned.
                </span>
              </div>
              <div className="space-y-2 border-t border-border pt-3">
                {toggles.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={!!ec[k]} onChange={(e) => setEc({ [k]: e.target.checked })} data-testid={`email-${k}`} /> {l}
                  </label>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
      </Collapse>

      <Collapse title="Proposal & Finance" icon="FileText" testId="collapse-proposal">
      <div className="bg-card border border-border rounded-md p-6 shadow-sm space-y-4" data-testid="proposal-settings">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="label-caps">Default customer annual bill (AUD)</label>
            <input type="number" value={s.default_annual_bill ?? 2400} onChange={(e) => setS({ ...s, default_annual_bill: parseFloat(e.target.value) })}
              data-testid="setting-default-bill" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
            <div className="text-[11px] text-muted-foreground mt-0.5">Used when the consultant leaves the bill blank.</div></div>
          <div><label className="label-caps">Google reviews — rating / count / link</label>
            <div className="flex gap-2 mt-1">
              <input type="number" step="0.1" value={s.reviews?.rating ?? ""} onChange={(e) => setS({ ...s, reviews: { ...s.reviews, rating: parseFloat(e.target.value) } })}
                data-testid="setting-review-rating" className="w-20 px-3 py-2 rounded-md border border-input bg-background" placeholder="4.9" />
              <input type="number" value={s.reviews?.count ?? ""} onChange={(e) => setS({ ...s, reviews: { ...s.reviews, count: parseInt(e.target.value) } })}
                data-testid="setting-review-count" className="w-24 px-3 py-2 rounded-md border border-input bg-background" placeholder="121" />
              <input value={s.reviews?.url || ""} onChange={(e) => setS({ ...s, reviews: { ...s.reviews, url: e.target.value } })}
                data-testid="setting-review-url" className="flex-1 px-3 py-2 rounded-md border border-input bg-background" placeholder="Google review link" />
            </div></div>
        </div>
        <div>
          <label className="label-caps">Terms & Conditions (shown on the proposal)</label>
          <textarea value={s.terms_and_conditions || ""} onChange={(e) => setS({ ...s, terms_and_conditions: e.target.value, terms_version: `v${Date.now()}` })}
            rows={5} data-testid="setting-terms" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm" />
        </div>
        <div className="border-t border-border pt-4">
          <div className="flex items-center gap-2 mb-2">
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input type="checkbox" checked={!!s.plenti?.enabled} onChange={(e) => setS({ ...s, plenti: { ...s.plenti, enabled: e.target.checked } })} data-testid="plenti-enabled" />
              Enable Plenti finance
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div><label className="label-caps">Monthly charge ($)</label>
              <input type="number" step="0.01" value={s.plenti?.monthly_charge ?? 6.99} onChange={(e) => setS({ ...s, plenti: { ...s.plenti, monthly_charge: parseFloat(e.target.value) } })}
                data-testid="plenti-monthly-charge" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
            <div><label className="label-caps">One-time charge ($)</label>
              <input type="number" value={s.plenti?.one_time_charge ?? 100} onChange={(e) => setS({ ...s, plenti: { ...s.plenti, one_time_charge: parseFloat(e.target.value) } })}
                data-testid="plenti-onetime-charge" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
          </div>
          <label className="label-caps">Finance terms (months → interest %)</label>
          <div className="space-y-2 mt-1">
            {(s.plenti?.terms || []).map((t, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input type="number" value={t.months} onChange={(e) => { const terms = [...s.plenti.terms]; terms[i] = { ...t, months: parseInt(e.target.value) }; setS({ ...s, plenti: { ...s.plenti, terms } }); }}
                  data-testid={`plenti-term-months-${i}`} className="w-24 px-3 py-2 rounded-md border border-input bg-background" placeholder="months" />
                <span className="text-muted-foreground text-sm">months →</span>
                <input type="number" step="0.01" value={t.percent} onChange={(e) => { const terms = [...s.plenti.terms]; terms[i] = { ...t, percent: parseFloat(e.target.value) }; setS({ ...s, plenti: { ...s.plenti, terms } }); }}
                  data-testid={`plenti-term-percent-${i}`} className="w-24 px-3 py-2 rounded-md border border-input bg-background" placeholder="%" />
                <span className="text-muted-foreground text-sm">%</span>
                <button onClick={() => { const terms = s.plenti.terms.filter((_, idx) => idx !== i); setS({ ...s, plenti: { ...s.plenti, terms } }); }} data-testid={`plenti-remove-term-${i}`} className="text-destructive p-1"><Icons.X size={16} /></button>
              </div>
            ))}
            <button onClick={() => setS({ ...s, plenti: { ...s.plenti, terms: [...(s.plenti?.terms || []), { months: 12, percent: 6.5 }] } })}
              data-testid="plenti-add-term" className="text-sm px-3 py-1.5 rounded-md bg-muted font-semibold flex items-center gap-1.5"><Icons.Plus size={14} /> Add term</button>
          </div>
        </div>
      </div>
      </Collapse>

      <button onClick={save} data-testid="settings-save"
        className="px-6 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] transition-all">Save Settings</button>

      <Collapse title="Email Templates (all automated emails)" icon="FileText" testId="collapse-templates"><EmailTemplates /></Collapse>

      <Collapse title="Lead Auto-Assignment" icon="Shuffle" testId="collapse-autoassign"><AutoAssignment /></Collapse>

      <Collapse title="WhatsApp Notifications" icon="MessageCircle" testId="collapse-whatsapp"><WhatsAppNotifications /></Collapse>

      <Collapse title="Uniden Phone Numbers" icon="Phone" testId="collapse-uniden"><UnidenNumbers /></Collapse>
      <Collapse title="Uniden Transcripts & Private Contacts" icon="FileText" testId="collapse-voc-features"><VocFeatures /></Collapse>

      <Collapse title="Stripe Payments" icon="CreditCard" testId="collapse-stripe"><StripePayments /></Collapse>


      <Collapse title="Facebook Lead Ads" icon="Facebook" testId="collapse-facebook"><FacebookLeadAds /></Collapse>

      <Collapse title="Google Drive Storage" icon="HardDrive" testId="collapse-gdrive"><GoogleDriveStorage /></Collapse>
    </div>
  );
}

const STRATEGIES = [
  ["weighted", "Weighted (%)", "Distribute leads by the percentage share you set for each person."],
  ["round_robin", "Round Robin (equal)", "Every included person (share > 0) gets an equal, rotating share."],
  ["by_solution", "By Interested Solution", "Route leads to a specific person based on the solution they want; unmatched leads fall back to the pool below."],
  ["by_postcode", "By Postcode", "Route leads to a specific person by postcode / postcode range; unmatched leads fall back to the pool below."],
];

function AutoAssignment() {
  const [aa, setAa] = useState(null);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});

  const load = () => {
    api.get("/auto-assign").then((r) => setAa(r.data)).catch(() => setAa(null));
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.active !== false)));
    api.get("/auto-assign/stats").then((r) => setStats(r.data?.counts || {})).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!aa) return null;

  const upd = (patch) => setAa((a) => ({ ...a, ...patch }));
  const weights = aa.weights || {};
  const totalWeight = Object.values(weights).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const isRR = aa.strategy === "round_robin";
  const showPool = ["weighted", "round_robin", "by_solution", "by_postcode"].includes(aa.strategy);

  const setWeight = (uid, val) => {
    const w = { ...weights };
    const n = parseFloat(val);
    if (!n || n <= 0) delete w[uid]; else w[uid] = n;
    upd({ weights: w });
  };
  const toggleRR = (uid, on) => {
    const w = { ...weights };
    if (on) w[uid] = 1; else delete w[uid];
    upd({ weights: w });
  };
  const distributeEvenly = () => {
    const n = users.length;
    if (!n) return;
    const base = Math.floor(100 / n);
    const rem = 100 - base * n;
    const w = {};
    users.forEach((u, i) => { w[u.id] = base + (i < rem ? 1 : 0); });
    upd({ weights: w });
  };

  const solutionUser = (sol) => (aa.solution_rules || []).find((r) => r.solution === sol)?.user_id || "";
  const setSolutionRule = (sol, uid) => {
    const rules = (aa.solution_rules || []).filter((r) => r.solution !== sol);
    if (uid) rules.push({ solution: sol, user_id: uid });
    upd({ solution_rules: rules });
  };

  const setPostcodeRule = (i, patch) => {
    const rules = [...(aa.postcode_rules || [])];
    rules[i] = { ...rules[i], ...patch };
    upd({ postcode_rules: rules });
  };
  const addPostcodeRule = () => upd({ postcode_rules: [...(aa.postcode_rules || []), { postcodes: "", user_id: "" }] });
  const removePostcodeRule = (i) => upd({ postcode_rules: (aa.postcode_rules || []).filter((_, idx) => idx !== i) });

  const save = async () => {
    try {
      const { data } = await api.put("/auto-assign", { ...aa, weights });
      setAa(data); toast.success("Auto-assignment saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const resetCounts = async () => {
    if (!window.confirm("Reset the distribution counters? This restarts the fair-share balance from zero.")) return;
    try { await api.post("/auto-assign/reset-counts"); setStats({}); toast.success("Counters reset"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const stratMeta = STRATEGIES.find((s) => s[0] === aa.strategy) || STRATEGIES[0];

  return (
    <div className="bg-card border border-border rounded-md p-6 shadow-sm space-y-5" data-testid="auto-assign-settings">
      <div className="flex items-center gap-2">
        <Icons.Shuffle size={18} className="text-secondary" />
        <h3 className="font-head font-semibold">Auto-Assignment of Leads</h3>
        {aa.enabled
          ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">ON</span>
          : <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-semibold">OFF</span>}
      </div>
      <p className="text-xs text-muted-foreground -mt-3">Automatically distribute new incoming leads to your team. Turn on/off anytime.</p>

      <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
        <input type="checkbox" checked={!!aa.enabled} onChange={(e) => upd({ enabled: e.target.checked })} data-testid="auto-assign-enabled" />
        Enable automatic lead assignment
      </label>

      <fieldset disabled={!aa.enabled} className={aa.enabled ? "min-w-0 border-0 p-0 m-0" : "min-w-0 border-0 p-0 m-0 opacity-50"}>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-caps">Distribution method</label>
            <select value={aa.strategy} onChange={(e) => upd({ strategy: e.target.value })}
              data-testid="auto-assign-strategy" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
              {STRATEGIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div className="text-[11px] text-muted-foreground mt-1">{stratMeta[2]}</div>
          </div>
          <div className="space-y-2">
            <label className="label-caps">Apply to</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!aa.apply_to_facebook} onChange={(e) => upd({ apply_to_facebook: e.target.checked })} data-testid="auto-assign-apply-fb" />
              Facebook lead ads (incoming)
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!aa.apply_to_manual} onChange={(e) => upd({ apply_to_manual: e.target.checked })} data-testid="auto-assign-apply-manual" />
              Manual leads left &quot;Unassigned&quot;
            </label>
          </div>
        </div>

        {/* Interested-solution routing */}
        {aa.strategy === "by_solution" && (
          <div className="mt-5 border-t border-border pt-4">
            <label className="label-caps">Route by interested solution</label>
            <div className="space-y-2 mt-2">
              {INTERESTED_SOLUTIONS.map((sol, i) => (
                <div key={sol} className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <span className="text-sm flex-1 min-w-0 truncate">{sol}</span>
                  <select value={solutionUser(sol)} onChange={(e) => setSolutionRule(sol, e.target.value)}
                    data-testid={`auto-assign-solution-${i}`} className="sm:w-56 px-3 py-2 rounded-md border border-input bg-background text-sm">
                    <option value="">— Fall back to pool —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Postcode routing */}
        {aa.strategy === "by_postcode" && (
          <div className="mt-5 border-t border-border pt-4">
            <label className="label-caps">Route by postcode</label>
            <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">Enter single postcodes or ranges, comma-separated. e.g. <b>3150, 3151-3159, 3200</b></p>
            <div className="space-y-2">
              {(aa.postcode_rules || []).map((r, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <input value={r.postcodes || ""} onChange={(e) => setPostcodeRule(i, { postcodes: e.target.value })}
                    data-testid={`auto-assign-postcode-spec-${i}`} placeholder="3150, 3151-3159"
                    className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-sm" />
                  <select value={r.user_id || ""} onChange={(e) => setPostcodeRule(i, { user_id: e.target.value })}
                    data-testid={`auto-assign-postcode-user-${i}`} className="sm:w-56 px-3 py-2 rounded-md border border-input bg-background text-sm">
                    <option value="">— Select person —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                  <button type="button" onClick={() => removePostcodeRule(i)} data-testid={`auto-assign-postcode-remove-${i}`} className="text-destructive p-1"><Icons.X size={16} /></button>
                </div>
              ))}
              <button type="button" onClick={addPostcodeRule} data-testid="auto-assign-postcode-add"
                className="text-sm px-3 py-1.5 rounded-md bg-muted font-semibold flex items-center gap-1.5"><Icons.Plus size={14} /> Add postcode rule</button>
            </div>
          </div>
        )}

        {/* Distribution pool / weights */}
        {showPool && (
          <div className="mt-5 border-t border-border pt-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <label className="label-caps">
                {aa.strategy === "weighted" ? "Distribution share (%)" : aa.strategy === "round_robin" ? "Rotation pool" : "Fallback pool"}
              </label>
              {!isRR && (
                <button type="button" onClick={distributeEvenly} data-testid="auto-assign-distribute"
                  className="text-xs px-2.5 py-1 rounded-md border border-border hover:bg-muted font-medium">Distribute evenly</button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mb-2">
              {isRR
                ? "Tick the people who should receive leads. Everyone ticked gets an equal, rotating share."
                : "Set a percentage for each person (out of 100%). People at 0% receive no leads. The engine keeps the running distribution true to these shares."}
            </p>
            <div className="rounded-md border border-border divide-y divide-border">
              {users.length === 0 && <div className="p-3 text-sm text-muted-foreground">No active users yet.</div>}
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-3 p-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{u.name}</div>
                    <div className="text-[11px] text-muted-foreground">{ROLE_LABELS[u.role] || u.role} · assigned so far: <b>{stats[u.id] || 0}</b></div>
                  </div>
                  {isRR ? (
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="checkbox" checked={(weights[u.id] || 0) > 0} onChange={(e) => toggleRR(u.id, e.target.checked)}
                        data-testid={`auto-assign-rr-${u.id}`} /> Include
                    </label>
                  ) : (
                    <div className="flex items-center gap-1">
                      <input type="number" min="0" max="100" step="1" value={weights[u.id] ?? ""} placeholder="0"
                        onChange={(e) => setWeight(u.id, e.target.value)} data-testid={`auto-assign-weight-${u.id}`}
                        className="w-20 px-2 py-1.5 rounded-md border border-input bg-background text-sm text-right" />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {!isRR && (
              <div className={`mt-2 text-xs font-semibold ${Math.abs(totalWeight - 100) < 0.5 ? "text-emerald-600" : "text-amber-600"}`} data-testid="auto-assign-total">
                Total: {Math.round(totalWeight * 10) / 10}%{Math.abs(totalWeight - 100) >= 0.5 && " — tip: shares are relative, but 100% keeps it intuitive."}
              </div>
            )}
          </div>
        )}
      </fieldset>

      <div className="flex flex-wrap gap-2 pt-1">
        <button onClick={save} data-testid="auto-assign-save"
          className="px-6 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] transition-all">Save Auto-Assignment</button>
        <button onClick={resetCounts} data-testid="auto-assign-reset"
          className="px-4 py-2.5 rounded-md border border-border hover:bg-muted font-medium text-sm flex items-center gap-1.5"><Icons.RotateCcw size={15} /> Reset counters</button>
      </div>
    </div>
  );
}

function StripePayments() {
  const [st, setSt] = useState(null);
  const [testSk, setTestSk] = useState("");
  const [testWh, setTestWh] = useState("");
  const [liveSk, setLiveSk] = useState("");
  const [liveWh, setLiveWh] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/integrations/stripe").then((r) => setSt(r.data)).catch(() => setSt({}));
  useEffect(() => { load(); }, []);
  if (!st) return null;
  const upd = (k, v) => setSt((s) => ({ ...s, [k]: v }));

  const save = async () => {
    const payload = {
      enabled: !!st.enabled, mode: st.mode || "test",
      test_publishable_key: st.test_publishable_key || "",
      live_publishable_key: st.live_publishable_key || "",
    };
    if (testSk) payload.test_secret_key = testSk;
    if (testWh) payload.test_webhook_secret = testWh;
    if (liveSk) payload.live_secret_key = liveSk;
    if (liveWh) payload.live_webhook_secret = liveWh;
    try {
      const { data } = await api.put("/integrations/stripe", payload);
      setSt(data); setTestSk(""); setTestWh(""); setLiveSk(""); setLiveWh("");
      toast.success("Stripe settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/stripe/test");
      toast.success(`Connected to Stripe (${data.mode}) — ${data.business_name}${data.charges_enabled ? "" : " — charges NOT enabled yet"}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="bg-card border border-border rounded-md p-6 shadow-sm space-y-4" data-testid="stripe-settings">
      <p className="text-xs text-muted-foreground">Connect your own Stripe account to collect customer deposits online. Deposits appear as a "Pay now" button on the customer's proposal and are auto-marked paid on your leads. Amounts are in AUD.</p>

      <div className="bg-primary/5 border border-primary/15 rounded-md p-3 text-xs text-muted-foreground space-y-1.5" data-testid="stripe-setup-help">
        <p className="font-semibold text-foreground">One-time Stripe setup</p>
        <p>1. In your <b>Stripe Dashboard → Developers → API keys</b>, copy the <b>Secret key</b> (<code>sk_live_…</code> for live, <code>sk_test_…</code> for test) and the matching <b>Publishable key</b> (<code>pk_…</code>).</p>
        <p>2. Go to <b>Developers → Webhooks → Add endpoint</b>, paste the URL below, and subscribe to <code>checkout.session.completed</code>, <code>checkout.session.async_payment_succeeded/failed</code>, <code>checkout.session.expired</code>.</p>
        <p>3. Copy the webhook's <b>Signing secret</b> (<code>whsec_…</code>) into the field below so paid deposits are confirmed automatically.</p>
        <p>4. Pick <b>Live mode</b> and tick <b>Enable</b> — then hit <b>Test connection</b>.</p>
      </div>

      <CopyRow label="Your webhook endpoint URL" value={st.webhook_url || ""} testid="stripe-webhook-url" />

      {/* Prominent live/test/blocked status banner */}
      {st.payments_blocked ? (
        <div className="flex items-start gap-3 rounded-lg border-2 border-red-300 bg-red-50 px-4 py-3" data-testid="stripe-banner-blocked">
          <Icons.ShieldAlert className="text-red-600 shrink-0 mt-0.5" size={22} />
          <div className="text-sm">
            <div className="font-extrabold tracking-wide text-red-700">STRIPE LIVE PAYMENTS UNAVAILABLE</div>
            <div className="text-red-700/90">Payment links and deposits are <b>blocked</b> until a Super Admin adds valid live keys below. No test-mode fallback is allowed in production — so no one can accidentally send a fake payment link.</div>
          </div>
        </div>
      ) : st.live_active ? (
        <div className="flex items-center gap-3 rounded-lg border-2 border-emerald-400 bg-emerald-50 px-4 py-3" data-testid="stripe-banner-live">
          <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span></span>
          <div className="text-sm">
            <div className="font-extrabold tracking-widest text-emerald-700">LIVE — REAL PAYMENTS</div>
            <div className="text-emerald-700/90">Customers are charged real money and funds settle to your connected Stripe account.</div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3" data-testid="stripe-banner-test">
          <Icons.FlaskConical className="text-amber-600 shrink-0" size={20} />
          <div className="text-sm">
            <div className="font-extrabold tracking-wide text-amber-700">TEST MODE — NO REAL MONEY</div>
            <div className="text-amber-700/90">{st.using_own_keys ? "Using your own test keys." : "Using the built-in sandbox (allowed in preview only)."} Payment links here do NOT collect real funds. Switch to Live mode with live keys to go live.</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
          <input type="checkbox" checked={!!st.enabled} onChange={(e) => upd("enabled", e.target.checked)} data-testid="stripe-enabled" />
          Use my own Stripe account
        </label>
        <div className="flex items-center gap-2 text-sm">
          <span className="label-caps">Mode</span>
          <select value={st.mode || "test"} onChange={(e) => upd("mode", e.target.value)} data-testid="stripe-mode"
            className="px-3 py-1.5 rounded-md border border-input bg-background text-sm">
            <option value="test">Test</option>
            <option value="live">Live</option>
          </select>
        </div>
        {st.using_own_keys && <span className="text-[11px] text-primary" data-testid="stripe-active-note">Active: your <b>{st.active_mode}</b> keys.</span>}
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div className="space-y-3 border border-border rounded-md p-4" data-testid="stripe-test-block">
          <div className="text-sm font-semibold">Test keys</div>
          <div><label className="label-caps">Publishable key (pk_test_…)</label>
            <input value={st.test_publishable_key || ""} onChange={(e) => upd("test_publishable_key", e.target.value)} data-testid="stripe-test-pk"
              className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm font-mono" placeholder="pk_test_…" /></div>
          <div><label className="label-caps">Secret key {st.test_secret_key_set && <span className="text-primary">(saved ✓ {st.test_secret_key_masked})</span>}</label>
            <input type="password" value={testSk} onChange={(e) => setTestSk(e.target.value)} data-testid="stripe-test-sk"
              className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm font-mono" placeholder={st.test_secret_key_set ? "•••••••• (leave blank to keep)" : "sk_test_…"} /></div>
          <div><label className="label-caps">Webhook signing secret {st.test_webhook_secret_set && <span className="text-primary">(saved ✓)</span>}</label>
            <input type="password" value={testWh} onChange={(e) => setTestWh(e.target.value)} data-testid="stripe-test-wh"
              className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm font-mono" placeholder={st.test_webhook_secret_set ? "•••••••• (leave blank to keep)" : "whsec_…"} /></div>
        </div>

        <div className="space-y-3 border border-border rounded-md p-4" data-testid="stripe-live-block">
          <div className="text-sm font-semibold">Live keys</div>
          <div><label className="label-caps">Publishable key (pk_live_…)</label>
            <input value={st.live_publishable_key || ""} onChange={(e) => upd("live_publishable_key", e.target.value)} data-testid="stripe-live-pk"
              className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm font-mono" placeholder="pk_live_…" /></div>
          <div><label className="label-caps">Secret key {st.live_secret_key_set && <span className="text-primary">(saved ✓ {st.live_secret_key_masked})</span>}</label>
            <input type="password" value={liveSk} onChange={(e) => setLiveSk(e.target.value)} data-testid="stripe-live-sk"
              className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm font-mono" placeholder={st.live_secret_key_set ? "•••••••• (leave blank to keep)" : "sk_live_…"} /></div>
          <div><label className="label-caps">Webhook signing secret {st.live_webhook_secret_set && <span className="text-primary">(saved ✓)</span>}</label>
            <input type="password" value={liveWh} onChange={(e) => setLiveWh(e.target.value)} data-testid="stripe-live-wh"
              className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm font-mono" placeholder={st.live_webhook_secret_set ? "•••••••• (leave blank to keep)" : "whsec_…"} /></div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
        <button onClick={save} data-testid="stripe-save" className="px-5 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-sm">Save Stripe Settings</button>
        <button onClick={verify} disabled={busy} data-testid="stripe-test-connection"
          className="px-4 py-2 rounded-md border border-secondary text-secondary font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50">
          <Icons.Plug size={14} /> {busy ? "Checking…" : "Test connection"}</button>
      </div>
    </div>
  );
}

const CopyRow = ({ label, value, testid }) => (
  <div>
    <label className="label-caps">{label}</label>
    <div className="flex gap-2 mt-1">
      <input readOnly value={value} data-testid={testid}
        className="flex-1 px-3 py-2 rounded-md border border-input bg-muted text-sm font-mono truncate" />
      <button type="button" onClick={async () => {
          try { await navigator.clipboard.writeText(value); toast.success("Copied"); }
          catch { toast.error("Copy failed — select the text and copy manually"); }
        }}
        data-testid={`${testid}-copy`} className="px-3 rounded-md border border-border hover:bg-muted" title="Copy"><Icons.Copy size={15} /></button>
    </div>
  </div>
);

function FacebookLeadAds() {
  const [fb, setFb] = useState(null);
  const [secretInput, setSecretInput] = useState("");
  const [tokenInput, setTokenInput] = useState("");
  const callbackUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/facebook`;

  useEffect(() => { api.get("/integrations/facebook").then((r) => setFb(r.data)).catch(() => setFb({})); }, []);
  if (!fb) return null;

  const upd = (k, v) => setFb((f) => ({ ...f, [k]: v }));
  const genToken = () => upd("verify_token", "grian_" + Math.random().toString(36).slice(2, 12) + Math.random().toString(36).slice(2, 8));

  const saveFb = async () => {
    const payload = {
      app_id: fb.app_id || "", page_id: fb.page_id || "",
      verify_token: fb.verify_token || "", graph_version: fb.graph_version || "v21.0",
      enabled: !!fb.enabled,
    };
    if (secretInput) payload.app_secret = secretInput;
    if (tokenInput) payload.page_access_token = tokenInput;
    try {
      const { data } = await api.put("/integrations/facebook", payload);
      setFb(data); setSecretInput(""); setTokenInput("");
      toast.success("Facebook settings saved");
    } catch { toast.error("Failed to save"); }
  };

  return (
    <div className="bg-card border border-border rounded-md p-6 shadow-sm space-y-4" data-testid="facebook-settings">
      <div className="flex items-center gap-2">
        <Icons.Facebook size={18} className="text-[#1877F2]" />
        <h3 className="font-head font-semibold">Facebook Lead Ads</h3>
        {fb.enabled && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">Enabled</span>}
      </div>
      <p className="text-xs text-muted-foreground -mt-2">Auto-capture leads from your Facebook/Instagram Lead Ad forms straight into the pipeline.</p>

      <div className="rounded-md bg-muted/50 border border-border p-3 space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Paste these into your Facebook app&apos;s Webhook config</div>
        <CopyRow label="Callback URL" value={callbackUrl} testid="fb-callback-url" />
        <div className="flex items-end gap-2">
          <div className="flex-1"><CopyRow label="Verify Token" value={fb.verify_token || ""} testid="fb-verify-token" /></div>
          <button type="button" onClick={genToken} className="px-3 py-2 rounded-md border border-border hover:bg-muted text-sm font-medium whitespace-nowrap" data-testid="fb-gen-token">Generate</button>
        </div>
        <div className="text-[11px] text-muted-foreground">Subscribe the Page to the <b>leadgen</b> field. The Callback URL uses your current environment — on the live site it shows your production URL.</div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="label-caps">App ID</label>
          <input value={fb.app_id || ""} onChange={(e) => upd("app_id", e.target.value)} data-testid="fb-app-id"
            className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
        <div><label className="label-caps">Page ID</label>
          <input value={fb.page_id || ""} onChange={(e) => upd("page_id", e.target.value)} data-testid="fb-page-id"
            className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
        <div><label className="label-caps">App Secret {fb.app_secret_set && <span className="text-emerald-600">✓ set</span>}</label>
          <input type="password" value={secretInput} onChange={(e) => setSecretInput(e.target.value)} data-testid="fb-app-secret"
            placeholder={fb.app_secret_set ? "•••••••• (leave blank to keep)" : "Enter App Secret"}
            className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
        <div><label className="label-caps">Page Access Token {fb.page_access_token_set && <span className="text-emerald-600">✓ set</span>}</label>
          <input type="password" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} data-testid="fb-page-token"
            placeholder={fb.page_access_token_set ? "•••••••• (leave blank to keep)" : "Long-lived Page token"}
            className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!fb.enabled} onChange={(e) => upd("enabled", e.target.checked)} data-testid="fb-enabled" />
        Enable Facebook lead capture
      </label>

      <button onClick={saveFb} data-testid="fb-save"
        className="px-6 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] transition-all">Save Facebook Settings</button>
    </div>
  );
}


function WhatsAppNotifications() {
  const [wa, setWa] = useState(null);
  const [tokenInput, setTokenInput] = useState("");
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/integrations/whatsapp").then((r) => setWa(r.data)).catch(() => setWa({})); }, []);
  if (!wa) return null;
  const upd = (k, v) => setWa((w) => ({ ...w, [k]: v }));

  const saveWa = async () => {
    const payload = {
      enabled: !!wa.enabled, phone_number_id: wa.phone_number_id || "",
      template_name: wa.template_name || "crm_alert", template_language: wa.template_language || "en",
      admin_numbers: wa.admin_numbers || [], notify_consultant: wa.notify_consultant !== false,
    };
    if (tokenInput) payload.access_token = tokenInput;
    try {
      const { data } = await api.put("/integrations/whatsapp", payload);
      setWa(data); setTokenInput("");
      toast.success("WhatsApp settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) return toast.error("Enter a phone number to test");
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/whatsapp/test", { to: testTo });
      toast.success(`Test WhatsApp sent to ${data.to} — check the phone`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="bg-card border border-border rounded-md p-6 shadow-sm space-y-4" data-testid="whatsapp-settings">
      <p className="text-xs text-muted-foreground">Mirror every CRM notification (quote opened, signed, deposit paid, new lead…) to WhatsApp via the official Meta Cloud API. Messages go to your fixed admin numbers plus the assigned consultant's mobile.</p>

      <div className="bg-primary/5 border border-primary/15 rounded-md p-3 text-xs text-muted-foreground space-y-1.5" data-testid="whatsapp-setup-help">
        <p className="font-semibold text-foreground">One-time Meta setup</p>
        <p>1. In <b>Meta Business Manager → WhatsApp Manager</b>, add a WhatsApp Business phone number and copy its <b>Phone Number ID</b> (not the WABA ID).</p>
        <p>2. In <b>Business Settings → System Users</b>, create a system user, assign your app + WhatsApp account, and generate a <b>permanent token</b> with <code>whatsapp_business_messaging</code> + <code>whatsapp_business_management</code>.</p>
        <p>3. In <b>WhatsApp Manager → Message Templates</b>, create a template and wait for approval:</p>
        <pre className="bg-background border border-border rounded p-2 whitespace-pre-wrap font-mono text-[11px]">{`Name: crm_alert   Category: Utility   Language: English
Body:
Grian Solar CRM: {{1}}
Lead: {{2}}
Open: {{3}}`}</pre>
      </div>

      <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
        <input type="checkbox" checked={!!wa.enabled} onChange={(e) => upd("enabled", e.target.checked)} data-testid="whatsapp-enabled" />
        Enable WhatsApp notifications
      </label>

      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="label-caps">Phone Number ID</label>
          <input value={wa.phone_number_id || ""} onChange={(e) => upd("phone_number_id", e.target.value)} data-testid="whatsapp-phone-id"
            className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" placeholder="e.g. 123456789012345" /></div>
        <div><label className="label-caps">Permanent access token {wa.access_token_set && <span className="text-primary">(saved ✓)</span>}</label>
          <input type="password" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} data-testid="whatsapp-token"
            className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" placeholder={wa.access_token_set ? "•••••••• (leave blank to keep)" : "Paste system-user token"} /></div>
        <div><label className="label-caps">Template name</label>
          <input value={wa.template_name || ""} onChange={(e) => upd("template_name", e.target.value)} data-testid="whatsapp-template"
            className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" placeholder="crm_alert" /></div>
        <div><label className="label-caps">Template language code</label>
          <input value={wa.template_language || ""} onChange={(e) => upd("template_language", e.target.value)} data-testid="whatsapp-language"
            className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" placeholder="en or en_US — must match the approved template" /></div>
      </div>

      <div><label className="label-caps">Fixed admin WhatsApp numbers (comma separated)</label>
        <input value={(wa.admin_numbers || []).join(", ")} data-testid="whatsapp-admin-numbers"
          onChange={(e) => upd("admin_numbers", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
          className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" placeholder="0433 123 456, +61400000000" />
        <div className="text-[11px] text-muted-foreground mt-0.5">Australian numbers are converted to +61 automatically.</div></div>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={wa.notify_consultant !== false} onChange={(e) => upd("notify_consultant", e.target.checked)} data-testid="whatsapp-notify-consultant" />
        Also message the assigned consultant (uses the mobile on their user profile)
      </label>

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
        <button onClick={saveWa} data-testid="whatsapp-save" className="px-5 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-sm">Save WhatsApp Settings</button>
        <div className="flex items-center gap-2 ml-auto">
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="whatsapp-test-to"
            className="w-44 px-3 py-2 rounded-md border border-input bg-background text-sm" placeholder="Test number" />
          <button onClick={sendTest} disabled={busy} data-testid="whatsapp-test-send"
            className="px-4 py-2 rounded-md border border-secondary text-secondary font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50">
            <Icons.Send size={14} /> {busy ? "Sending…" : "Send test"}</button>
        </div>
      </div>
    </div>
  );
}

function UnidenNumbers() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/integrations/uniden-numbers").then((r) => setCfg(r.data)).catch(() => setCfg({ numbers: [], users: [] })); }, []);
  if (!cfg) return null;

  const users = (cfg.users || []).filter((u) => u.active !== false);
  const numbers = cfg.numbers || [];

  const setNumbers = (arr) => setCfg((c) => ({ ...c, numbers: arr }));
  const updNumber = (i, patch) => setNumbers(numbers.map((n, idx) => (idx === i ? { ...n, ...patch } : n)));
  const addNumber = () => setNumbers([...numbers, { number: "", assigned_user_ids: [] }]);
  const removeNumber = (i) => setNumbers(numbers.filter((_, idx) => idx !== i));
  const toggleAssign = (i, uid) => {
    const cur = numbers[i].assigned_user_ids || [];
    updNumber(i, { assigned_user_ids: cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid] });
  };
  const userName = (uid) => (users.find((u) => u.id === uid) || {}).name || uid;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        numbers: numbers.filter((n) => (n.number || "").trim()).map((n) => ({ number: n.number.trim(), assigned_user_ids: n.assigned_user_ids || [] })),
        default_number: cfg.default_number || "",
        marketing_number: cfg.marketing_number || "",
        marketing_user_id: cfg.marketing_user_id || "",
      };
      const { data } = await api.put("/integrations/uniden-numbers", payload);
      setCfg(data);
      toast.success("Phone numbers saved");
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  return (
    <div className="bg-card border border-border rounded-md p-6 shadow-sm space-y-5" data-testid="uniden-settings">
      <div className="flex items-start gap-2">
        <p className="text-xs text-muted-foreground flex-1">Manage every Uniden/VOCPhone number and assign each one to the user(s) who send SMS &amp; make calls from it. A number can be shared by several users. When a consultant messages a lead, the CRM sends from their assigned number — falling back to the default below if they have none.</p>
        <span className={`text-[11px] px-2 py-1 rounded-full whitespace-nowrap ${cfg.voc_connected ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="uniden-voc-status">
          {cfg.voc_connected ? "VOCPhone connected" : "VOCPhone not connected"}
        </span>
      </div>

      <div className="space-y-3">
        {numbers.map((n, i) => (
          <div key={i} className="border border-border rounded-md p-3 space-y-2" data-testid="uniden-number-row">
            <div className="flex items-center gap-2">
              <Icons.Phone size={15} className="text-secondary shrink-0" />
              <input value={n.number} onChange={(e) => updNumber(i, { number: e.target.value })} data-testid={`uniden-number-input-${i}`}
                className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-sm font-medium" placeholder="e.g. 0488 860 990" />
              <button onClick={() => removeNumber(i)} data-testid={`uniden-number-remove-${i}`}
                className="p-2 rounded-md border border-border text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors"><Icons.Trash2 size={15} /></button>
            </div>
            <div>
              <div className="label-caps mb-1">Assigned to</div>
              <div className="flex flex-wrap gap-1.5" data-testid={`uniden-assign-${i}`}>
                {users.length === 0 && <span className="text-xs text-muted-foreground">No users yet</span>}
                {users.map((u) => {
                  const on = (n.assigned_user_ids || []).includes(u.id);
                  return (
                    <button key={u.id} type="button" onClick={() => toggleAssign(i, u.id)}
                      data-testid={`uniden-assign-${i}-${u.id}`}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:border-primary/40"}`}>
                      {on && <Icons.Check size={11} className="inline mr-1 -mt-0.5" />}{u.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
        <button onClick={addNumber} data-testid="uniden-add-number"
          className="text-sm px-4 py-2 rounded-md border border-dashed border-border text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors flex items-center gap-1.5">
          <Icons.Plus size={15} /> Add number
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 border-t border-border pt-4">
        <div>
          <label className="label-caps">Default sender number (fallback)</label>
          <select value={cfg.default_number || ""} onChange={(e) => setCfg((c) => ({ ...c, default_number: e.target.value }))}
            data-testid="uniden-default-number" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm">
            <option value="">— None —</option>
            {numbers.filter((n) => (n.number || "").trim()).map((n, i) => <option key={i} value={n.number}>{n.number}</option>)}
          </select>
          <div className="text-[11px] text-muted-foreground mt-0.5">Used when a user has no assigned number.</div>
        </div>
      </div>

      <div className="bg-primary/5 border border-primary/15 rounded-md p-3 space-y-3" data-testid="uniden-marketing">
        <p className="text-xs font-semibold text-foreground">Marketing / company number</p>
        <p className="text-[11px] text-muted-foreground">Pick a dedicated number (and the admin who owns it) to send marketing content on the company's behalf.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-caps">Marketing number</label>
            <select value={cfg.marketing_number || ""} onChange={(e) => setCfg((c) => ({ ...c, marketing_number: e.target.value }))}
              data-testid="uniden-marketing-number" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm">
              <option value="">— None —</option>
              {numbers.filter((n) => (n.number || "").trim()).map((n, i) => <option key={i} value={n.number}>{n.number}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps">Marketing admin</label>
            <select value={cfg.marketing_user_id || ""} onChange={(e) => setCfg((c) => ({ ...c, marketing_user_id: e.target.value }))}
              data-testid="uniden-marketing-user" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm">
              <option value="">— None —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {cfg.webhooks && (
        <div className="bg-muted/30 border border-border rounded-md p-3" data-testid="uniden-webhooks">
          <p className="text-xs font-medium text-foreground mb-1.5">Uniden webhook URLs (paste into your Uniden/VOCPhone portal)</p>
          {Object.entries(cfg.webhooks).map(([k, v]) => (
            <div key={k} className="mb-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.replace("_", " ")}</div>
              <code className="text-[11px] break-all bg-background border border-border rounded px-1.5 py-1 block">{v}</code>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-border pt-4">
        <button onClick={save} disabled={saving} data-testid="uniden-save"
          className="px-5 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60">
          {saving ? "Saving…" : "Save Phone Numbers"}</button>
      </div>
    </div>
  );
}


function GoogleDriveStorage() {
  const [gd, setGd] = useState(null);
  const [secretInput, setSecretInput] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [drives, setDrives] = useState(null);
  const [sdInput, setSdInput] = useState("");
  const [folderInput, setFolderInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/integrations/google-drive")
    .then((r) => { setGd(r.data); setSdInput(r.data.shared_drive_id || ""); setFolderInput(r.data.root_folder_id || ""); })
    .catch(() => setGd({}));
  useEffect(() => { load(); }, []);
  if (!gd) return null;

  const upd = (k, v) => setGd((g) => ({ ...g, [k]: v }));

  const saveCreds = async () => {
    const payload = { client_id: gd.client_id || "", enabled: gd.enabled !== false };
    if (secretInput) payload.client_secret = secretInput;
    try {
      const { data } = await api.put("/integrations/google-drive", payload);
      setGd((g) => ({ ...g, ...data })); setSecretInput("");
      toast.success("Google credentials saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const payload = { client_id: gd.client_id || "" };
      if (secretInput) payload.client_secret = secretInput;
      await api.put("/integrations/google-drive", payload);
      const { data } = await api.get("/integrations/google-drive/auth-url");
      window.location.href = data.auth_url;
    } catch (e) { toast.error(errMsg(e)); setConnecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Drive? New uploads will fall back to the database until you reconnect.")) return;
    try { await api.post("/integrations/google-drive/disconnect"); await load(); setDrives(null); toast.success("Google Drive disconnected"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const loadDrives = async () => {
    setBusy(true);
    try { const { data } = await api.get("/integrations/google-drive/shared-drives"); setDrives(data.shared_drives || []); }
    catch (e) { setDrives([]); toast.error(errMsg(e)); }
    setBusy(false);
  };

  const saveRoot = async () => {
    if (!sdInput.trim()) { toast.error("Select or paste a Shared Drive first"); return; }
    setBusy(true);
    try {
      const { data } = await api.put("/integrations/google-drive/root",
        { shared_drive_id: sdInput.trim(), root_folder_id: folderInput.trim() });
      setGd((g) => ({ ...g, ...data }));
      toast.success(`Storage set to “${data.root_folder_name || data.shared_drive_name}”`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const testUpload = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/google-drive/test-upload");
      toast.success(`✓ Test file created in “${data.location}” and removed. Uploads are working.`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const badge = gd.active
    ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">Active</span>
    : gd.connected
      ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold">Setup: choose Shared Drive</span>
      : <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-semibold">Not connected</span>;

  return (
    <div className="bg-card border border-border rounded-md p-6 shadow-sm space-y-4" data-testid="gdrive-settings">
      <div className="flex items-center gap-2">
        <Icons.HardDrive size={18} className="text-emerald-600" />
        <h3 className="font-head font-semibold">Google Drive Storage</h3>
        {badge}
      </div>
      <p className="text-xs text-muted-foreground -mt-2">Store uploaded files (documents, site photos, datasheets) in a Google <b>Shared Drive</b>. Each customer gets their own folder (e.g. “GSP1234 - John Smith”); product media goes in “Products”. Lead &amp; customer data always stays in the CRM database.</p>

      {!gd.connected ? (
        <>
          <div className="rounded-md bg-muted/50 border border-border p-3 space-y-3">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Add this Redirect URI in your Google OAuth client</div>
            <CopyRow label="Authorized redirect URI" value={gd.redirect_uri} testid="gdrive-redirect-uri" />
            <div className="text-[11px] text-muted-foreground">Google Cloud Console → Credentials → your OAuth client (Web application) → <b>Authorized redirect URIs</b>. Add your live-site URI too when you go live.</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="label-caps">Client ID</label>
              <input value={gd.client_id || ""} onChange={(e) => upd("client_id", e.target.value)} data-testid="gdrive-client-id"
                placeholder="xxxx.apps.googleusercontent.com"
                className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
            <div><label className="label-caps">Client Secret {gd.client_secret_set && <span className="text-emerald-600">✓ set</span>}</label>
              <input type="password" value={secretInput} onChange={(e) => setSecretInput(e.target.value)} data-testid="gdrive-client-secret"
                placeholder={gd.client_secret_set ? "•••••••• (leave blank to keep)" : "Enter Client Secret"}
                className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={saveCreds} data-testid="gdrive-save"
              className="px-5 py-2.5 rounded-md border border-border hover:bg-muted font-semibold">Save Credentials</button>
            <button onClick={connect} disabled={connecting || (!gd.client_id) || (!gd.client_secret_set && !secretInput)} data-testid="gdrive-connect"
              className="px-6 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] transition-all disabled:opacity-50 disabled:translate-y-0 flex items-center gap-2">
              <Icons.Link2 size={16} /> {connecting ? "Opening Google…" : "Connect Google Account"}
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground">Save your Client ID &amp; Secret, then click Connect and approve access in the Google window.</p>
        </>
      ) : (
        <>
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Icons.UserCheck size={15} className="text-emerald-600" /> Connected as <b className="text-foreground">{gd.email || "your Google account"}</b>
          </div>

          <div className="rounded-md bg-muted/50 border border-border p-4 space-y-3" data-testid="gdrive-root-picker">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Storage location</div>
            <div>
              <label className="label-caps">Shared Drive <span className="text-destructive">*</span></label>
              <div className="flex gap-2 mt-1">
                <select value={(drives && drives.some((d) => d.id === sdInput)) ? sdInput : ""} onChange={(e) => setSdInput(e.target.value)} disabled={!drives || busy}
                  data-testid="gdrive-drive-select"
                  className="flex-1 px-3 py-2 rounded-md border border-input bg-background disabled:opacity-60">
                  <option value="">{drives ? "— Select a Shared Drive —" : "Click “Load” to list drives"}</option>
                  {(drives || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <button type="button" onClick={loadDrives} disabled={busy} data-testid="gdrive-load-drives"
                  className="px-3 py-2 rounded-md border border-border hover:bg-muted text-sm font-medium whitespace-nowrap disabled:opacity-50">Load</button>
              </div>
              <input value={sdInput} onChange={(e) => setSdInput(e.target.value)} data-testid="gdrive-shared-drive-id"
                placeholder="…or paste the Shared Drive ID (0AJ…)"
                className="w-full mt-2 px-3 py-2 rounded-md border border-input bg-background font-mono text-sm" />
            </div>
            <div>
              <label className="label-caps">Root Folder ID <span className="text-muted-foreground normal-case">(optional — a folder inside the Shared Drive)</span></label>
              <input value={folderInput} onChange={(e) => setFolderInput(e.target.value)} data-testid="gdrive-root-folder-id"
                placeholder="Leave blank to use the Shared Drive root"
                className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background font-mono text-sm" />
            </div>
            <button onClick={saveRoot} disabled={busy} data-testid="gdrive-save-root"
              className="px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] transition-all disabled:opacity-50">
              {busy ? "Checking…" : "Save storage location"}
            </button>
            <p className="text-[11px] text-muted-foreground">The connected account must be a member of the Shared Drive with at least <b>Content manager</b> access. A Root Folder, if given, must live inside the selected Shared Drive — we validate both before saving.</p>
          </div>

          {gd.active ? (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 space-y-2" data-testid="gdrive-active">
              <div className="flex items-center gap-2"><Icons.CheckCircle2 size={16} />
                Uploads save to <b>{gd.shared_drive_name}</b> (Shared Drive){gd.root_folder_name ? <> → <b>{gd.root_folder_name}</b> folder</> : " root"} → per-customer folders.</div>
              <button onClick={testUpload} disabled={busy} data-testid="gdrive-test-upload"
                className="px-4 py-2 rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50 flex items-center gap-2 w-fit">
                <Icons.UploadCloud size={15} /> {busy ? "Testing…" : "Test Upload"}
              </button>
            </div>
          ) : (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-center gap-2">
              <Icons.AlertTriangle size={16} /> Select your Shared Drive above to finish setup. Until then, uploads are saved in the database.
            </div>
          )}

          <button onClick={disconnect} data-testid="gdrive-disconnect"
            className="px-4 py-2 rounded-md border border-border hover:bg-muted text-sm font-medium">Disconnect Google account</button>
        </>
      )}
    </div>
  );
}



function StorageUsage({ limit, onLimit, onSave }) {
  const [st, setSt] = useState(null);
  const [mig, setMig] = useState(null);
  const load = () => { api.get("/system/storage").then((r) => setSt(r.data)).catch(() => {}); api.get("/system/file-migration").then((r) => setMig(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!mig?.running) return; const t = setInterval(load, 5000); return () => clearInterval(t); }, [mig?.running]);
  const startMigration = async () => {
    try { await api.post("/system/file-migration/start"); toast.success("Migration started — files move to Google Drive in the background"); setTimeout(load, 1500); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!st) return <div className="bg-card border border-border rounded-md p-6 text-sm text-muted-foreground">Loading storage stats…</div>;
  const tone = st.level === "critical" ? "bg-red-600" : st.level === "warning" ? "bg-amber-500" : st.level === "notice" ? "bg-yellow-400" : "bg-emerald-500";
  const msg = { critical: "Critical — storage is almost full. Free space now (archive recordings/files to Google Drive or delete old data).", warning: "Warning — approaching capacity. Plan a clean-up or upgrade soon.", notice: "Notice — over 75% used.", ok: "Healthy" }[st.level];
  const fmt = (mb) => mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb} MB`;
  return (
    <div className="bg-card border border-border rounded-md p-6 shadow-sm space-y-4" data-testid="storage-usage">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-2xl font-head font-extrabold" data-testid="storage-used">{fmt(st.used_mb)} <span className="text-base font-medium text-muted-foreground">/ {fmt(st.limit_mb)}</span></div>
          <div className="text-sm text-muted-foreground">Usage: <b data-testid="storage-percent">{st.percent}%</b> · Remaining: <b data-testid="storage-remaining">{fmt(st.remaining_mb)}</b> · {st.objects?.toLocaleString()} records</div>
        </div>
        <button onClick={load} className="text-sm px-3 py-1.5 rounded-md border border-border flex items-center gap-1.5"><Icons.RefreshCw size={14} /> Refresh</button>
      </div>
      <div className="h-3 rounded-full bg-muted overflow-hidden"><div className={`h-full ${tone} transition-all`} style={{ width: `${Math.min(100, st.percent)}%` }} /></div>
      <div className={`text-sm flex items-start gap-2 ${st.level === "ok" ? "text-emerald-700" : st.level === "critical" ? "text-red-700" : "text-amber-700"}`} data-testid="storage-warning">
        {st.level === "ok" ? <Icons.CheckCircle2 size={16} className="mt-0.5" /> : <Icons.AlertTriangle size={16} className="mt-0.5" />} {msg}
      </div>
      <div className="grid sm:grid-cols-2 gap-4 text-xs text-muted-foreground">
        <div>Data size: <b className="text-foreground">{fmt(st.data_mb)}</b> · On-disk (incl. indexes): <b className="text-foreground">{fmt(st.storage_mb)}</b></div>
        <div className="flex items-center gap-2">Plan limit (MB):
          <input type="number" value={limit ?? 1024} onChange={(e) => onLimit(+e.target.value)} data-testid="storage-limit-input" className="w-24 px-2 py-1 rounded-md border border-input bg-background text-foreground" />
          <button onClick={onSave} className="px-2 py-1 rounded-md bg-primary text-primary-foreground font-semibold">Save</button></div>
      </div>
      <div className="text-xs text-muted-foreground">Set the limit to match your MongoDB hosting plan — providers don't expose the cap via API. Warnings: 75% notice · 90% warning · 95% critical.</div>
      {mig && (
        <div className="border border-border rounded-md p-4 space-y-2" data-testid="file-migration">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm">
              <div className="font-semibold flex items-center gap-2"><Icons.HardDriveUpload size={15} className="text-primary" /> Files stored in database → Google Drive</div>
              <div className="text-xs text-muted-foreground mt-0.5" data-testid="file-migration-stats">
                <b>{mig.in_db}</b> files ({fmt(mig.in_db_mb)}) still in the database · <b>{mig.on_drive}</b> on Google Drive
                {mig.failed_permanently > 0 && <span className="text-red-600"> · {mig.failed_permanently} failed</span>}
              </div>
            </div>
            <button onClick={startMigration} disabled={mig.running || !mig.drive_connected || mig.in_db === 0} data-testid="file-migration-start"
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5">
              {mig.running ? <Icons.Loader2 size={14} className="animate-spin" /> : <Icons.CloudUpload size={14} />} {mig.running ? `Migrating… ${mig.moved || 0}/${mig.total || "?"}` : "Migrate to Google Drive now"}
            </button>
          </div>
          {mig.running && <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${mig.total ? Math.min(100, ((mig.moved || 0) + (mig.failed || 0)) / mig.total * 100) : 0}%` }} /></div>}
          {!mig.drive_connected && <div className="text-xs text-amber-700 flex items-center gap-1" data-testid="file-migration-hint"><Icons.AlertTriangle size={13} /> Connect Google Drive (section below) — once connected, new uploads and call recordings go straight to Drive and existing database files are migrated automatically every 15 minutes.</div>}
          {mig.drive_connected && mig.in_db === 0 && <div className="text-xs text-emerald-700 flex items-center gap-1"><Icons.CheckCircle2 size={13} /> All files live on Google Drive.</div>}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-1 text-xs">
        {st.collections.slice(0, 6).map((c) => <div key={c.name} className="flex justify-between border-b border-border/50 py-1"><span>{c.name}</span><span className="font-medium">{fmt(c.size_mb)} · {c.count.toLocaleString()}</span></div>)}
      </div>
    </div>
  );
}
